<template>
  <a-modal
    v-model:visible="visible"
    :title="t('workspaceSettings.workspace.editTitle')"
    :width="500"
    :ok-loading="loading"
    :on-before-ok="handleSubmit"
    @cancel="handleCancel"
  >
    <a-form ref="formRef" :model="form" layout="vertical">
      <a-form-item
        :label="t('workspaceSettings.workspace.name')"
        field="name"
        :rules="[
          {
            required: true,
            message: t('workspaceSettings.workspace.nameRequired'),
          },
        ]"
      >
        <a-input
          v-model="form.name"
          :placeholder="t('workspaceSettings.workspace.namePlaceholder')"
          :max-length="50"
          show-word-limit
        />
      </a-form-item>

      <a-form-item
        :label="t('workspaceSettings.workspace.description')"
        field="description"
      >
        <a-textarea
          v-model="form.description"
          :placeholder="t('workspaceSettings.workspace.descriptionPlaceholder')"
          :max-length="200"
          :auto-size="{ minRows: 3, maxRows: 5 }"
          show-word-limit
        />
      </a-form-item>
    </a-form>
  </a-modal>
</template>

<script setup lang="ts">
  import { ref, watch, computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import Message from '@/utils/message';
  import type { Workspace } from '@/types/workspace';
  import { updateWorkspaceApi } from '@/api/workspace';

  const { t } = useI18n();

  const props = defineProps<{
    modelValue: boolean;
    workspace?: Workspace | null;
  }>();

  const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void;
    (e: 'success'): void;
  }>();

  const visible = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value),
  });

  const formRef = ref();
  const loading = ref(false);
  const form = ref({
    name: '',
    description: '',
  });

  // 监听弹窗打开，初始化表单
  watch(visible, (newVal) => {
    if (newVal && props.workspace) {
      form.value = {
        name: props.workspace.name || '',
        description: props.workspace.description || '',
      };
    }
  });

  const handleCancel = () => {
    visible.value = false;
    formRef.value?.resetFields();
  };

  const handleSubmit = async () => {
    if (!formRef.value || !props.workspace) return false;

    try {
      const valid = await formRef.value.validate();
      if (valid) return false;

      loading.value = true;
      await updateWorkspaceApi({
        workspace_uuid: props.workspace.workspace_uuid,
        name: form.value.name,
        description: form.value.description,
      });

      Message.success(
        t('workspaceSettings.workspace.updateSuccess') || '更新成功'
      );
      emit('success');
      handleCancel();
      return true;
    } catch (err: any) {
      Message.error(
        err?.response?.data?.msg ||
          t('workspaceSettings.workspace.updateFailed') ||
          '更新失败'
      );
      return false;
    } finally {
      loading.value = false;
    }
  };
</script>

<script lang="ts">
  export default {
    name: 'EditWorkspaceModal',
  };
</script>

<style lang="less" scoped></style>
